import { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { getSubmission } from '../api/formSubmissions';
import { QUESTION_TYPE_LABELS } from '../types/forms';
import type { SubmissionAnswer, SubmissionDetail, SubmissionQuestion } from '../types/formSubmissions';
import { evaluateFormVisibility } from '../utils/formVisibility';
import { answersFromDetail, answersToVisibilityInput } from '../utils/formSubmissionAnswers';
import { formatAnswerDisplay } from '../utils/formResponseDisplay';
import { ShiftCloseDetailModal } from './ShiftCloseHistoryPanel';
import type { Store } from '../types/stores';

// Fase 25A-R: vista de EVIDENCIA de una respuesta enviada (SOLO LECTURA absoluta). Reutiliza el detalle
// version-pinned (23B), el mapa de respuestas y el evaluador de visibilidad (23A/23B) para mostrar
// EXACTAMENTE lo que se vio y contesto en su momento. No edita, no reenvia, no inventa "Sin respuesta"
// para preguntas ocultas.

// Zona horaria de la TIENDA (patrón 17A-4), no la del dispositivo.
const fmtDateTime = (iso: string | null, timeZone: string) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso)) : '—');
const fmtDate = (iso: string | null, timeZone: string) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone }).format(new Date(iso)) : '—');
const contextLabel = (c: 'MANUAL' | 'SHIFT_CLOSE') => (c === 'SHIFT_CLOSE' ? 'Corte de turno' : 'Manual');

// Valor legible por tipo (nunca IDs/JSON/booleanos crudos). null/vacio -> "Sin respuesta" (pregunta
// visible que no fue contestada; las ocultas ni siquiera se renderizan).
function AnswerValue({ question, answer }: { question: SubmissionQuestion; answer: SubmissionAnswer | undefined }) {
  const d = formatAnswerDisplay(question, answer);
  if (d.isEmpty) return <em className="response-detail__empty">{d.text}</em>;
  return <span className={d.multiline ? 'response-detail__text' : undefined}>{d.text}</span>;
}

export function ResponseDetailView({ submissionId, onBack, canOpenShiftClose, timeZone, stores, backLabel }: { submissionId: string; onBack: () => void; canOpenShiftClose: boolean; timeZone: string; stores: Store[]; backLabel?: string }) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  // "Ver Corte" abre el detalle del corte EN SITIO (modal encima de esta vista): al cerrarlo el
  // usuario sigue exactamente donde estaba, sin viajar a la sección de Cortes y perder el contexto.
  const [shiftCloseOpen, setShiftCloseOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(undefined);
    getSubmission(submissionId)
      .then((d) => { if (alive) setDetail(d.submission); })
      .catch((e) => { if (alive) setError(getErrorMessage(e, 'No fue posible cargar la respuesta.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [submissionId, reloadToken]);

  const visible = useMemo(() => (detail ? evaluateFormVisibility(detail, answersToVisibilityInput(answersFromDetail(detail))) : null), [detail]);
  const answersByQuestion = useMemo(() => new Map((detail?.answers ?? []).map((a) => [a.questionId, a])), [detail]);

  const voided = detail?.shiftClose?.status === 'VOIDED';

  return (
    <section className="response-detail" aria-label="Detalle de respuesta">
      <div className="response-detail__topbar">
        <button className="text-button" onClick={onBack} type="button">{backLabel ?? '← Volver a respuestas'}</button>
      </div>

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {loading ? <div className="inline-loading"><span className="spinner" />Cargando respuesta…</div> : detail && (
        <>
          <header className="response-detail__header">
            <h2>{detail.formName}</h2>
            <span className="form-badge form-badge--active">Enviado</span>
            <dl className="response-detail__meta">
              <div><dt>Fecha de envío</dt><dd>{fmtDateTime(detail.submittedAt, timeZone)}</dd></div>
              <div><dt>Tienda</dt><dd>{detail.storeName}</dd></div>
              <div><dt>Respondió</dt><dd>{detail.respondent.name}</dd></div>
              <div><dt>Origen</dt><dd>{contextLabel(detail.context)}</dd></div>
              <div><dt>Versión</dt><dd>v{detail.versionNumber}</dd></div>
              {detail.shiftClose && (
                <div>
                  <dt>Corte de turno</dt>
                  <dd className="response-detail__shiftclose">
                    <strong>{detail.shiftClose.folio ?? 'Corte'} del {fmtDate(detail.shiftClose.startAt, timeZone)}</strong>
                    {voided
                      ? <span className="form-badge form-badge--inactive" title="El corte relacionado fue anulado">Corte anulado</span>
                      : <span className="form-badge">Corte registrado</span>}
                    {canOpenShiftClose && <button className="text-button" onClick={() => setShiftCloseOpen(true)} type="button">Ver Corte →</button>}
                  </dd>
                </div>
              )}
            </dl>
            {voided && <p className="response-detail__note">El Corte de turno relacionado fue anulado. La respuesta enviada se conserva intacta como evidencia.</p>}
          </header>

          <div className="response-detail__body">
            {detail.sections
              .slice()
              .sort((a, b) => a.position - b.position)
              .map((section) => {
                const questions = detail.questions
                  .filter((q) => q.sectionId === section.id && visible?.visibleQuestionIds.has(q.id))
                  .sort((a, b) => a.position - b.position);
                if (questions.length === 0) return null;
                return (
                  <section key={section.id} className="response-detail__section" aria-label={section.title}>
                    <h3>{section.title}</h3>
                    {section.description && <p className="response-detail__section-desc">{section.description}</p>}
                    <dl className="response-detail__answers">
                      {questions.map((q) => (
                        <div key={q.id} className="response-detail__answer">
                          <dt>{q.text}{q.isRequired && <span className="response-detail__req" title="Obligatoria"> *</span>}<span className="response-detail__qtype">{QUESTION_TYPE_LABELS[q.type]}</span></dt>
                          <dd><AnswerValue question={q} answer={answersByQuestion.get(q.id)} /></dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                );
              })}
          </div>
        </>
      )}
      {shiftCloseOpen && detail?.shiftClose && (
        <ShiftCloseDetailModal
          shiftCloseId={detail.shiftClose.id}
          stores={stores}
          onClose={() => setShiftCloseOpen(false)}
          onVoided={() => { setShiftCloseOpen(false); setReloadToken((token) => token + 1); }}
        />
      )}
    </section>
  );
}
